import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Loader2, ImagePlus, CheckCircle2, X, Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import { useAuth } from "../context/AuthContext";
import { createEvent, updateEvent, fetchOwnEvent, compressEventPhoto, EVENT_PHOTO_ERROR } from "../lib/eventApi";
import { getOrCreateProfile } from "../lib/profileApi";
import { getListingStatus, startListingCheckout, consumeListing, confirmListingPayment, authorizeUpdate, consumeUpdate, LISTING_PACKS } from "../lib/listingApi";
import { coverUrlsFor } from "../lib/events";
import { COUNTRIES, countryName } from "../lib/countries";

const fieldClass =
  "border-white/10 bg-[#121212] text-white placeholder:text-white/30 focus-visible:ring-[#ff2a00]";

const WEB_RE = /^https?:\/\/.+\..+/i;
const IG_RE = /^https?:\/\/(www\.)?instagram\.com\/.+/i;
const FB_RE = /^https?:\/\/(www\.)?(facebook\.com|fb\.com|m\.facebook\.com)\/.+/i;
const MAX_DURATION_MS = 120 * 60 * 60 * 1000; // 5 days / 120 hours

const CATEGORIES = [
  "Cars",
  "Motorsport",
  "Motorcycles",
  "Shows",
  "Meets & Coffee",
  "Track Days",
];

const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

const EMPTY = {
  name: "",
  category: "",
  description: "",
  start_date: "",
  start_time: "",
  end_date: "",
  end_time: "",
  country_code: "GB",
  venue_name: "",
  address: "",
  postcode: "",
  ticket_url: "",
  website_url: "",
  instagram_url: "",
  facebook_url: "",
};

const DRAFT_PREFIX = "roadrnr_event_draft";

function combine(date, time) {
  if (!date) return null;
  return `${date}T${time || "00:00"}:00`;
}

function splitIso(iso) {
  if (!iso) return { date: "", time: "" };
  const [d, rest] = String(iso).split("T");
  let time = rest ? rest.slice(0, 5) : "";
  if (time === "00:00") time = "";
  return { date: d || "", time };
}

const EMPTY_OCC = { start_date: "", start_time: "", end_date: "", end_time: "" };
// Stable signature of the full occurrence set (used to detect a qualifying edit).
const occToSig = (list) =>
  list
    .map((o) => `${o.start_date || ""}T${o.start_time || ""}|${o.end_date || ""}T${o.end_time || ""}`)
    .sort()
    .join(";");

export default function AddEvent() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const editMode = Boolean(editId);
  const { user, loading: authLoading } = useAuth();
  const fileRef = useRef(null);

  const [form, setForm] = useState(EMPTY);
  const [isFree, setIsFree] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [existing, setExisting] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(editMode);
  const [hydrated, setHydrated] = useState(false);
  const [listing, setListing] = useState(null);
  const [buyingPack, setBuyingPack] = useState(null);
  // Additional event dates (occurrence 1 is the primary form above; these are 2..10).
  const [extraOccurrences, setExtraOccurrences] = useState([]);
  const [existingOccSig, setExistingOccSig] = useState("");

  const setOcc = (i, key) => (e) =>
    setExtraOccurrences((list) =>
      list.map((o, idx) => (idx === i ? { ...o, [key]: e?.target ? e.target.value : e } : o))
    );
  const addOccurrence = () =>
    setExtraOccurrences((l) => (l.length + 1 < 10 ? [...l, { ...EMPTY_OCC }] : l));
  const removeOccurrence = (i) =>
    setExtraOccurrences((l) => l.filter((_, idx) => idx !== i));

  const draftKey = editMode ? `${DRAFT_PREFIX}_edit_${editId}` : `${DRAFT_PREFIX}_add`;

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  // Load the listing allowance/admin/paid state, and (after a £1.99 checkout)
  // verify the returned Stripe session so the paid entitlement is available.
  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      const sid = new URLSearchParams(window.location.search).get("session_id");
      if (sid) {
        await confirmListingPayment(sid);
        window.history.replaceState({}, "", window.location.pathname);
      }
      try {
        const s = await getListingStatus();
        if (active) setListing(s);
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      active = false;
    };
  }, [user]);

  // Add mode: a normal (non-admin) user must have BOTH Organiser / Business Name
  // and Short Bio saved before entering the Add Event flow. Runs once the listing
  // status (admin flag) is known; admins are exempt.
  useEffect(() => {
    if (editMode || !user || !listing || listing.is_admin) return;
    let active = true;
    (async () => {
      try {
        const p = await getOrCreateProfile(user.id);
        const complete =
          !!(p.organiser_name && p.organiser_name.trim()) &&
          !!(p.organiser_bio && p.organiser_bio.trim());
        if (active && !complete) {
          toast.error("Complete your Organiser / Business Name and Short Bio before adding an event.");
          navigate("/profile", { replace: true });
        }
      } catch {
        /* non-fatal: server also enforces this at listing consume */
      }
    })();
    return () => {
      active = false;
    };
  }, [editMode, user, listing, navigate]);

  // Add mode: restore any locally saved draft once on mount.
  useEffect(() => {
    if (editMode) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (d?.form) setForm({ ...EMPTY, ...d.form });
        if (typeof d?.isFree === "boolean") setIsFree(d.isFree);
        if (Array.isArray(d?.extraOccurrences)) setExtraOccurrences(d.extraOccurrences);
      }
    } catch {
      /* ignore malformed draft */
    }
    setHydrated(true);
  }, [editMode, draftKey]);

  useEffect(() => {
    if (!editMode || !user) return;
    let active = true;
    (async () => {
      try {
        const row = await fetchOwnEvent(editId);
        if (!active) return;
        if (!row || row.owner_id !== user.id) {
          toast.error("You can only edit your own events.");
          navigate("/my-events", { replace: true });
          return;
        }
        const s = splitIso(row.start_at);
        const e = splitIso(row.end_at);
        // Occurrence rows (if any) are the source of truth for a multi-date event;
        // occurrence 1 becomes the primary form, the rest become extra blocks.
        const occRows = Array.isArray(row.occurrences) ? row.occurrences : [];
        const occParsed = occRows
          .map((o) => {
            const os = splitIso(o.start_at);
            const oe = splitIso(o.end_at);
            return { start_date: os.date, start_time: os.time, end_date: oe.date, end_time: oe.time };
          })
          .sort((a, b) =>
            `${a.start_date}T${a.start_time}`.localeCompare(`${b.start_date}T${b.start_time}`)
          );
        const primary = occParsed.length
          ? occParsed[0]
          : { start_date: s.date, start_time: s.time, end_date: e.date, end_time: e.time };
        setForm({
          name: row.name || "",
          category: row.category || "",
          description: row.description || "",
          start_date: primary.start_date,
          start_time: primary.start_time,
          end_date: primary.end_date,
          end_time: primary.end_time,
          country_code: row.country_code || "GB",
          venue_name: row.venue_name || "",
          address: row.address || "",
          postcode: row.postcode || "",
          ticket_url: row.ticket_url || "",
          website_url: row.website_url || "",
          instagram_url: row.instagram_url || "",
          facebook_url: row.facebook_url || "",
        });
        const extras = occParsed.slice(1);
        setExtraOccurrences(extras);
        setExistingOccSig(occToSig([primary, ...extras]));
        setIsFree(!!row.is_free);
        setExisting(row);
        // Restore any locally saved unsaved changes over the Supabase values.
        try {
          const raw = localStorage.getItem(draftKey);
          if (raw) {
            const d = JSON.parse(raw);
            if (d?.form) setForm((prev) => ({ ...prev, ...d.form }));
            if (typeof d?.isFree === "boolean") setIsFree(d.isFree);
            if (Array.isArray(d?.extraOccurrences)) setExtraOccurrences(d.extraOccurrences);
          }
        } catch {
          /* ignore malformed draft */
        }
        const covers = await coverUrlsFor([editId]);
        if (active && covers[editId]) setImagePreview(covers[editId]);
      } catch (err) {
        toast.error("Could not load this event.");
        navigate("/my-events", { replace: true });
      } finally {
        if (active) {
          setLoadingEdit(false);
          setHydrated(true);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [editMode, editId, user, navigate, draftKey]);

  // Persist the in-progress form locally so leaving/reloading the page does not
  // lose unsaved information. Cleared on successful save.
  useEffect(() => {
    if (!hydrated || done) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({ form, isFree, extraOccurrences }));
    } catch {
      /* storage may be unavailable */
    }
  }, [form, isFree, extraOccurrences, hydrated, done, draftKey]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const chooseImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error("Please choose a JPEG, PNG or WebP image.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image is too large (max 10 MB).");
      return;
    }
    // Compress now (max 1200px, WebP 70%) so only the compressed photo is ever
    // uploaded. If it can't be compressed, reject it — the original is never used.
    let photo;
    try {
      photo = await compressEventPhoto(file);
    } catch (err) {
      toast.error(err?.message || EVENT_PHOTO_ERROR);
      return;
    }
    setImageFile(photo);
    setImagePreview(URL.createObjectURL(photo));
  };

  const isAdmin = !!listing?.is_admin;
  const canPublish =
    editMode || isAdmin || (listing && (listing.free_remaining > 0 || listing.paid_available > 0));
  const freeRemaining = listing?.free_remaining ?? 0;
  const paidAvailable = listing?.paid_available ?? 0;

  // Buy a listing-credit pack (1/10/20) via the shared Stripe Checkout. On return
  // the load effect confirms the session so the credit(s) become available.
  const buyPack = async (priceId) => {
    setBuyingPack(priceId);
    try {
      const res = await startListingCheckout("/add-event", priceId);
      if (res?.native) {
        toast.success("Listing credits added");
        try {
          const s = await getListingStatus();
          setListing(s);
        } catch {
          /* ignore */
        }
        setBuyingPack(null);
        return;
      }
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      throw new Error("Could not start checkout.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not start checkout.");
      setBuyingPack(null);
    }
  };

  // LIVE NOW is a dynamic state (any occurrence start <= now < end). While an
  // occurrence is live, date/time/location edits are locked (UI + server-enforced).
  const existingIsLive = (() => {
    if (!editMode || !existing) return false;
    const now = Date.now();
    const occRows = Array.isArray(existing.occurrences) ? existing.occurrences : [];
    const list = occRows.length ? occRows : [{ start_at: existing.start_at, end_at: existing.end_at }];
    return list.some((o) => {
      const s = o.start_at ? new Date(o.start_at).getTime() : null;
      const e = o.end_at ? new Date(o.end_at).getTime() : null;
      return s != null && e != null && s <= now && now < e;
    });
  })();
  const lockSchedule = existingIsLive;

  // A qualifying edit = any change to the occurrence set (add/remove/date-time
  // change) OR the address/location. Counts as ONE new listing regardless of how
  // many occurrence changes were made in the save.
  const qualifyingChange = (() => {
    if (!editMode || !existing) return false;
    const currentSig = occToSig([
      {
        start_date: form.start_date,
        start_time: form.start_time,
        end_date: form.end_date,
        end_time: form.end_time,
      },
      ...extraOccurrences,
    ]);
    const scheduleChanged = currentSig !== existingOccSig;
    const locationChanged =
      (form.venue_name || "").trim() !== (existing.venue_name || "") ||
      (form.address || "").trim() !== (existing.address || "") ||
      (form.postcode || "").trim() !== (existing.postcode || "") ||
      (form.country_code || "GB") !== (existing.country_code || "GB");
    return scheduleChanged || locationChanged;
  })();

  const needPayForUpdate =
    editMode && qualifyingChange && !isAdmin && freeRemaining === 0 && paidAvailable === 0;

  // Rolling 1-year-ahead cap for normal users (admins exempt). Computed dynamically.
  const maxStartDateStr = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  })();

  const validate = () => {
    if (!form.name || !form.category || !form.start_date || !form.start_time || !form.venue_name || !form.postcode) {
      toast.error("Please fill in event name, category, start date, start time, venue and postcode.");
      return false;
    }
    if (!form.address.trim()) {
      toast.error("Please enter the event address.");
      return false;
    }
    if (!form.end_date) {
      toast.error("Please set the event end date and time.");
      return false;
    }
    const start = new Date(combine(form.start_date, form.start_time));
    const end = new Date(combine(form.end_date, form.end_time));
    if (!(end > start)) {
      toast.error("The end must be after the start.");
      return false;
    }
    if (!isAdmin && end - start > MAX_DURATION_MS) {
      toast.error("Events can run for a maximum of 5 days.");
      return false;
    }
    if (!isAdmin) {
      const maxStart = new Date();
      maxStart.setFullYear(maxStart.getFullYear() + 1);
      if (start > maxStart) {
        toast.error("Events can only be listed up to 1 year in advance.");
        return false;
      }
    }
    // Additional occurrences: validate each and keep them within 12 months of the
    // primary date; max 10 total.
    if (extraOccurrences.length + 1 > 10) {
      toast.error("An event can have at most 10 dates.");
      return false;
    }
    const TWELVE_MONTHS_MS = 366 * 24 * 60 * 60 * 1000;
    for (let i = 0; i < extraOccurrences.length; i++) {
      const o = extraOccurrences[i];
      const label = `event date ${i + 2}`;
      if (!o.start_date || !o.start_time || !o.end_date) {
        toast.error(`Please complete the start date, start time and end date for ${label}.`);
        return false;
      }
      const os = new Date(combine(o.start_date, o.start_time));
      const oe = new Date(combine(o.end_date, o.end_time));
      if (!(oe > os)) {
        toast.error(`For ${label}, the end must be after the start.`);
        return false;
      }
      if (!isAdmin && oe - os > MAX_DURATION_MS) {
        toast.error(`For ${label}, events can run for a maximum of 5 days.`);
        return false;
      }
      if (!isAdmin) {
        const maxStart2 = new Date();
        maxStart2.setFullYear(maxStart2.getFullYear() + 1);
        if (os > maxStart2) {
          toast.error(`For ${label}, events can only be listed up to 1 year in advance.`);
          return false;
        }
      }
      if (Math.abs(os.getTime() - start.getTime()) > TWELVE_MONTHS_MS) {
        toast.error("All event dates must fall within 12 months of the first date.");
        return false;
      }
    }
    if (form.website_url.trim() && !WEB_RE.test(form.website_url.trim())) {
      toast.error("Please enter a valid website URL (https://…).");
      return false;
    }
    if (form.ticket_url.trim() && !WEB_RE.test(form.ticket_url.trim())) {
      toast.error("Please enter a valid ticket URL (https://…).");
      return false;
    }
    if (form.instagram_url.trim() && !IG_RE.test(form.instagram_url.trim())) {
      toast.error("Instagram must be a valid instagram.com URL.");
      return false;
    }
    if (form.facebook_url.trim() && !FB_RE.test(form.facebook_url.trim())) {
      toast.error("Facebook must be a valid facebook.com URL.");
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // ── EDIT MODE ──────────────────────────────────────────────────────────
    if (editMode) {
      // LIVE lock (fields are also disabled in the UI).
      if (qualifyingChange && lockSchedule) {
        toast.error("The date, time and location can't be changed while an event is live.");
        return;
      }
      setSubmitting(true);
      // Pre-save: authorize a qualifying date/time/location change (server-enforced
      // LIVE lock + listing entitlement). Ordinary edits skip this entirely.
      if (qualifyingChange && !isAdmin) {
        try {
          await authorizeUpdate(editId);
        } catch (err) {
          const status = err?.response?.status;
          if (status === 402) {
            try {
              const res = await startListingCheckout(`/edit-event/${editId}`);
              if (res?.native) {
                // The in-app purchase completed. Re-check the entitlement and carry straight on
                // to the save instead of asking the user to tap the button again (support
                // #279162: the second tap, still labelled "£1.99", looked like a failed payment).
                let authorized = false;
                try {
                  await authorizeUpdate(editId);
                  authorized = true;
                } catch {
                  /* fall back to asking for one more tap below */
                }
                if (!authorized) {
                  try {
                    setListing(await getListingStatus());
                  } catch {
                    /* ignore */
                  }
                  toast.success("Listing credit added — tap Save to apply your changes.");
                  setSubmitting(false);
                  return;
                }
                toast.success("Payment received — saving your changes…");
              } else if (res?.checkout_url) {
                window.location.href = res.checkout_url;
                return;
              } else {
                throw new Error("Could not start checkout.");
              }
            } catch (e2) {
              toast.error(e2?.response?.data?.detail || e2?.message || "Could not start checkout.");
              setSubmitting(false);
              return;
            }
          } else {
            toast.error(err?.response?.data?.detail || "Could not save changes.");
            setSubmitting(false);
            return;
          }
        }
      }
      try {
        const payload = {
          ...form,
          start_at: combine(form.start_date, form.start_time),
          end_at: combine(form.end_date, form.end_time),
          occurrences: [
            { start_date: form.start_date, start_time: form.start_time, end_date: form.end_date, end_time: form.end_time },
            ...extraOccurrences,
          ].map((o) => ({ start_at: combine(o.start_date, o.start_time), end_at: combine(o.end_date, o.end_time) })),
          is_free: isFree,
        };
        const { imageWarning } = await updateEvent(editId, payload, imageFile, existing, !isAdmin);
        if (imageWarning) toast.warning(imageWarning);
        // Consume exactly ONE listing AFTER the qualifying update saved successfully.
        if (qualifyingChange && !isAdmin) {
          try {
            await consumeUpdate(editId);
          } catch (err) {
            toast.warning(
              err?.response?.data?.detail || "Saved, but the listing could not be recorded."
            );
          }
        }
        try {
          localStorage.removeItem(draftKey);
        } catch {
          /* ignore */
        }
        setDone(true);
      } catch (err) {
        toast.error(err.message || "Could not update the event. Please try again.");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // ── ADD MODE (unchanged) ───────────────────────────────────────────────
    if (!canPublish) {
      setSubmitting(true);
      try {
        const res = await startListingCheckout();
        if (res?.native) {
          toast.success("Listing credit added — tap Publish to continue.");
          try {
            const s = await getListingStatus();
            setListing(s);
          } catch {
            /* ignore */
          }
          setSubmitting(false);
          return;
        }
        if (res?.checkout_url) {
          window.location.href = res.checkout_url;
          return;
        }
        throw new Error("Could not start checkout.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || err?.message || "Could not start checkout.");
        setSubmitting(false);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        start_at: combine(form.start_date, form.start_time),
        end_at: combine(form.end_date, form.end_time),
        occurrences: [
          { start_date: form.start_date, start_time: form.start_time, end_date: form.end_date, end_time: form.end_time },
          ...extraOccurrences,
        ].map((o) => ({ start_at: combine(o.start_date, o.start_time), end_at: combine(o.end_date, o.end_time) })),
        is_free: isFree,
      };
      const { event, imageWarning } = await createEvent(user.id, payload, imageFile, !isAdmin);
      if (imageWarning) toast.warning(imageWarning);
      if (event?.id) {
        try {
          await consumeListing(event.id);
        } catch (err) {
          toast.warning(
            err?.response?.data?.detail || "Event saved, but the listing could not be recorded."
          );
        }
      }
      try {
        localStorage.removeItem(draftKey);
      } catch {
        /* ignore */
      }
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Could not add the event. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loadingEdit) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#050505]">
        <Loader2 className="h-6 w-6 animate-spin text-[#ff2a00]" />
      </div>
    );
  }
  if (!user) return null;

  if (done) {
    return (
      <div
        data-testid="addevent-success"
        className="flex min-h-[100dvh] flex-col items-center justify-center bg-[#050505] px-8 text-center"
      >
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#ff2a00]/15">
          <CheckCircle2 className="h-8 w-8 text-[#ff2a00]" />
        </div>
        <h1 className="font-display mt-6 text-2xl font-extrabold tracking-tight text-white">
          {editMode ? "Event updated." : "Event added successfully."}
        </h1>
        <button
          data-testid="addevent-done-discover"
          onClick={() => navigate(editMode ? "/my-events" : "/")}
          className="mt-8 rounded-full bg-[#ff2a00] px-8 py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98]"
        >
          {editMode ? "Back to My Events" : "Back to Discover"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="addevent-page" className="min-h-[100dvh] bg-[#050505] px-6 pb-16 pt-6">
      <button
        data-testid="addevent-back"
        onClick={() => navigate(editMode ? "/my-events" : "/profile")}
        className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#0d0d0d] transition-transform duration-200 active:scale-90"
        aria-label="Back"
      >
        <ChevronLeft className="h-5 w-5 text-white" />
      </button>

      <div className="mx-auto mt-6 max-w-md">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-white">
          {editMode ? "Edit event" : "Add an event"}
        </h1>
        <p className="mt-1 text-sm text-white/50">
          {editMode ? "Update your event details." : "List your event on RoadRnr."}
        </p>

        <form onSubmit={submit} className="mt-7 space-y-5">
          {/* Cover image (one only) */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
              Event cover image
            </Label>
            <button
              type="button"
              data-testid="addevent-image-btn"
              onClick={() => fileRef.current?.click()}
              className="relative flex aspect-[16/9] w-full items-center justify-center overflow-hidden rounded-2xl border border-dashed border-white/15 bg-[#121212]"
            >
              {imagePreview ? (
                <>
                  <img src={imagePreview} alt="Cover" className="h-full w-full object-cover" />
                  <span
                    onClick={(ev) => {
                      ev.stopPropagation();
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                    className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-[#050505]/80 text-white"
                  >
                    <X className="h-4 w-4" />
                  </span>
                </>
              ) : (
                <div className="flex flex-col items-center gap-2 text-white/40">
                  <ImagePlus className="h-7 w-7" />
                  <span className="text-xs">JPEG, PNG or WebP</span>
                </div>
              )}
            </button>
            <input
              ref={fileRef}
              data-testid="addevent-image-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={chooseImage}
              className="hidden"
            />
          </div>

          <Field label="Event name" required>
            <Input
              data-testid="addevent-name"
              value={form.name}
              onChange={set("name")}
              placeholder="e.g. Goodwood Festival of Speed"
              className={fieldClass}
            />
          </Field>

          <Field label="Category" required>
            <Select value={form.category} onValueChange={set("category")}>
              <SelectTrigger data-testid="addevent-category" className="border-white/10 bg-[#121212] text-white">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent className="rr-cat-select border-white/10 bg-[#1e1e1e] text-white">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} className="focus:bg-[#ff2a00]/10 focus:text-[#ff2a00] data-[highlighted]:bg-[#ff2a00]/10 data-[highlighted]:text-[#ff2a00] data-[state=checked]:text-[#ff2a00] data-[state=checked]:font-semibold">
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Description">
            <Textarea
              data-testid="addevent-description"
              value={form.description}
              onChange={set("description")}
              placeholder="Tell people about the event..."
              rows={3}
              className={fieldClass}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
            <Field label="Start date" required>
              <Input data-testid="addevent-start-date" type="date" disabled={lockSchedule} max={!isAdmin ? maxStartDateStr : undefined} value={form.start_date} onChange={set("start_date")} className={`${fieldClass} min-w-0 disabled:opacity-50 disabled:cursor-not-allowed`} />
            </Field>
            <Field label="Start time" required>
              <Input data-testid="addevent-start-time" type="time" disabled={lockSchedule} value={form.start_time} onChange={set("start_time")} className={`${fieldClass} min-w-0 disabled:opacity-50 disabled:cursor-not-allowed`} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
            <Field label="End date" required>
              <Input data-testid="addevent-end-date" type="date" disabled={lockSchedule} min={form.start_date || undefined} value={form.end_date} onChange={set("end_date")} className={`${fieldClass} min-w-0 disabled:opacity-50 disabled:cursor-not-allowed`} />
            </Field>
            <Field label="End time" required>
              <Input data-testid="addevent-end-time" type="time" disabled={lockSchedule} value={form.end_time} onChange={set("end_time")} className={`${fieldClass} min-w-0 disabled:opacity-50 disabled:cursor-not-allowed`} />
            </Field>
          </div>

          {/* Multiple event dates (occurrences) — same event, extra dates only. */}
          <div className="space-y-3" data-testid="occurrences-section">
            <p className="text-xs font-semibold uppercase tracking-wide text-white/50">
              Does this event repeat?
            </p>
            {extraOccurrences.map((occ, i) => (
              <div
                key={i}
                data-testid={`occurrence-${i}`}
                className="space-y-3 rounded-2xl border border-white/10 bg-[#0d0d0d] p-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-white/60">Date {i + 2}</span>
                  <button
                    type="button"
                    data-testid={`occurrence-remove-${i}`}
                    disabled={lockSchedule}
                    onClick={() => removeOccurrence(i)}
                    className="text-xs font-semibold text-[#ff6a4d] transition-opacity disabled:opacity-40"
                  >
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
                  <Field label="Start date" required>
                    <Input data-testid={`occurrence-start-date-${i}`} type="date" disabled={lockSchedule} max={!isAdmin ? maxStartDateStr : undefined} value={occ.start_date} onChange={setOcc(i, "start_date")} className={`${fieldClass} min-w-0 disabled:opacity-50 disabled:cursor-not-allowed`} />
                  </Field>
                  <Field label="Start time" required>
                    <Input data-testid={`occurrence-start-time-${i}`} type="time" disabled={lockSchedule} value={occ.start_time} onChange={setOcc(i, "start_time")} className={`${fieldClass} min-w-0 disabled:opacity-50 disabled:cursor-not-allowed`} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
                  <Field label="End date" required>
                    <Input data-testid={`occurrence-end-date-${i}`} type="date" disabled={lockSchedule} min={occ.start_date || undefined} value={occ.end_date} onChange={setOcc(i, "end_date")} className={`${fieldClass} min-w-0 disabled:opacity-50 disabled:cursor-not-allowed`} />
                  </Field>
                  <Field label="End time" required>
                    <Input data-testid={`occurrence-end-time-${i}`} type="time" disabled={lockSchedule} value={occ.end_time} onChange={setOcc(i, "end_time")} className={`${fieldClass} min-w-0 disabled:opacity-50 disabled:cursor-not-allowed`} />
                  </Field>
                </div>
              </div>
            ))}
            {extraOccurrences.length + 1 < 10 && (
              <button
                type="button"
                data-testid="occurrence-add"
                disabled={lockSchedule}
                onClick={addOccurrence}
                className="w-full rounded-full border border-[#ff2a00]/40 bg-[#ff2a00]/10 py-2.5 text-sm font-bold text-[#ff2a00] transition-colors hover:bg-[#ff2a00]/15 disabled:opacity-40"
              >
                + Add another event date
              </button>
            )}
          </div>

          <Field label="Country" required>
            <Popover open={countryOpen} onOpenChange={setCountryOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="addevent-country"
                  role="combobox"
                  aria-expanded={countryOpen}
                  disabled={lockSchedule}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-white/10 bg-[#121212] px-3 text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {countryName(form.country_code)}
                  <ChevronsUpDown className="h-4 w-4 opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] border-white/10 bg-[#1e1e1e] p-0 text-white">
                <Command className="bg-[#1e1e1e] text-white">
                  <CommandInput placeholder="Search country..." className="text-white" />
                  <CommandList>
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup>
                      {COUNTRIES.map((c) => (
                        <CommandItem
                          key={c.code}
                          value={c.name}
                          data-testid={`country-option-${c.code}`}
                          onSelect={() => {
                            set("country_code")(c.code);
                            setCountryOpen(false);
                          }}
                          className="cursor-pointer text-white aria-selected:bg-white/10"
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              form.country_code === c.code ? "opacity-100" : "opacity-0"
                            }`}
                          />
                          {c.name} <span className="ml-auto text-white/40">{c.code}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Field>

          <Field label="Venue name" required>
            <Input data-testid="addevent-venue" disabled={lockSchedule} value={form.venue_name} onChange={set("venue_name")} placeholder="e.g. Goodwood Motor Circuit" className={`${fieldClass} disabled:opacity-50 disabled:cursor-not-allowed`} />
          </Field>
          <Field label="Address" required>
            <Input data-testid="addevent-address" disabled={lockSchedule} value={form.address} onChange={set("address")} placeholder="Street, town/city" className={`${fieldClass} disabled:opacity-50 disabled:cursor-not-allowed`} />
          </Field>
          <Field label="Postcode / ZIP code" required>
            <Input data-testid="addevent-postcode" disabled={lockSchedule} value={form.postcode} onChange={set("postcode")} placeholder={form.country_code === "US" ? "e.g. 90210" : "e.g. PO18 0PH"} className={`${fieldClass} disabled:opacity-50 disabled:cursor-not-allowed`} />
          </Field>

          {editMode && (
            <p data-testid="addevent-schedule-caption" className="-mt-1 text-xs leading-snug text-white/40">
              Changing the date, time or location counts as a new listing. These details can't be
              changed while an event is live.
            </p>
          )}

          {/* Free / Paid */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
              Entry
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="addevent-free"
                onClick={() => setIsFree(true)}
                className={`rounded-full border py-3 text-sm font-bold transition-colors ${
                  isFree ? "border-[#ff2a00] bg-[#ff2a00] text-white" : "border-white/10 bg-[#121212] text-white/60"
                }`}
              >
                Free
              </button>
              <button
                type="button"
                data-testid="addevent-paid"
                onClick={() => setIsFree(false)}
                className={`rounded-full border py-3 text-sm font-bold transition-colors ${
                  !isFree ? "border-[#ff2a00] bg-[#ff2a00] text-white" : "border-white/10 bg-[#121212] text-white/60"
                }`}
              >
                Paid
              </button>
            </div>
          </div>

          <Field label="Ticket / booking URL">
            <Input data-testid="addevent-ticket-url" value={form.ticket_url} onChange={set("ticket_url")} placeholder="https://" className={fieldClass} />
          </Field>
          <Field label="Event website URL">
            <Input data-testid="addevent-website-url" value={form.website_url} onChange={set("website_url")} placeholder="https://" className={fieldClass} />
          </Field>
          <Field label="Event Instagram URL">
            <Input data-testid="addevent-instagram-url" value={form.instagram_url} onChange={set("instagram_url")} placeholder="https://instagram.com/..." className={fieldClass} />
          </Field>
          <Field label="Event Facebook URL">
            <Input data-testid="addevent-facebook-url" value={form.facebook_url} onChange={set("facebook_url")} placeholder="https://facebook.com/..." className={fieldClass} />
          </Field>

          {!editMode && !isAdmin && listing && !canPublish && (
            <div
              data-testid="addevent-paywall"
              className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4"
            >
              <p className="text-sm font-semibold text-white">
                You've used your 2 free event listings this month
              </p>
              <p className="mt-1 text-xs text-white/50">
                Buy listing credits to publish more. Purchased credits never expire.
              </p>
              <div className="mt-3 space-y-2.5">
                {LISTING_PACKS.map((p) => (
                  <button
                    key={p.priceId}
                    type="button"
                    data-testid={`paywall-buy-${p.credits}`}
                    onClick={() => buyPack(p.priceId)}
                    disabled={!!buyingPack}
                    className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-[#121212] px-4 py-3 transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
                  >
                    <span className="text-sm font-bold text-white">
                      {p.label}
                    </span>
                    {buyingPack === p.priceId ? (
                      <Loader2 className="h-5 w-5 animate-spin text-[#ff2a00]" />
                    ) : (
                      <span className="text-sm font-bold text-[#ff2a00]">{p.price}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            data-testid="addevent-submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : editMode ? (needPayForUpdate ? "Update event · £1.99" : "Save changes") : canPublish ? "Add event" : "Add another event · £1.99"}
          </button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
      {label} {required && <span className="text-[#ff2a00]">*</span>}
    </Label>
    {children}
  </div>
);
